package com.eladfinish.bible.contest.screens.pastQuizzes

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.luminance
import com.eladfinish.bible.contest.analytics.crashlytics.AppCrashlytics
import com.eladfinish.bible.contest.analytics.usageStatsEnabled
import com.eladfinish.bible.contest.common.gender.getHebrewGender
import com.eladfinish.bible.contest.platform.isAndroid
import com.eladfinish.bible.contest.ui.ads.AdBannerSuppression
import com.eladfinish.bible.contest.ui.common.rememberSafeUriHandler
import com.eladfinish.bible.contest.ui.components.WebView
import com.eladfinish.bible.contest.ui.components.webViewBridgeOf
import com.eladfinish.bible.contest.ui.extensions.LocalNavigationBarInset
import com.eladfinish.bible.contest.ui.extensions.navigationBarInsetPadding
import com.russhwolf.settings.Settings
import org.koin.compose.koinInject

/**
 * "חידונים אמיתיים": past quizzes (district, national, head-to-head), answered solo in the district-quiz
 * web app's exam mode.
 * The web page does the quiz and keeps its own progress (localStorage), so an exam survives
 * leaving the screen. System back walks back through the page's screens, then leaves.
 * Links out of the page (the booklets on its credits page) open in the system browser.
 * [duelCode] opens a ראש בראש room (a duel link, ExamDuel.kt) instead of the exam's home page.
 */
@Composable
fun PastQuizzesScreen(
    onNavigateBack: () -> Unit,
    duelCode: String? = null,
    config: GetPastQuizzesConfigUseCase = koinInject(),
    settings: Settings = koinInject(),
    adBannerSuppression: AdBannerSuppression = koinInject(),
) {
    val isDarkTheme = isAppInDarkTheme()
    val isDuelEnabled = remember { !config.isDuelDisabled() }
    // voice answers: the page's mic runs on this platform's speech recognizer (ExamVoice.kt)
    val voiceBridge = rememberExamVoiceBridge()?.takeUnless { config.isVoiceDisabled() }
    // ראש בראש's invitation goes to the system share sheet (ExamDuel.kt)
    val shareBridge = rememberExamShareBridge()?.takeIf { isDuelEnabled }
    // the page's usage events go to AppMetrica too, with the user's consent (ExamAnalytics.kt)
    val usageStats = remember { settings.usageStatsEnabled() }
    val analyticsBridge = rememberExamAnalyticsBridge(usageStats = usageStats && !config.isAppMetricaDisabled())
    val bridge = remember(voiceBridge, shareBridge, analyticsBridge) { webViewBridgeOf(voiceBridge, shareBridge, analyticsBridge) }
    // the page uses the app's theme and the font picked in the app's settings
    val url = remember(isDarkTheme, duelCode) {
        config.url(
            isDarkTheme = isDarkTheme,
            fontPreference = settings.examFontPreference(),
            isVoiceEnabled = voiceBridge != null,
            hebrewGender = settings.getHebrewGender(),
            usageStats = usageStats,
            duelCode = duelCode?.takeIf { isDuelEnabled },
        )
    }
    val pageOrigin = remember(url) { url.substringBefore("/#").substringBefore('?').trimEnd('/') + "/" }
    // After a rotation the new WebView opens the page's screen the user was on, not the page's home.
    var lastPage by rememberSaveable(url) { mutableStateOf<String?>(null) }
    val pageToOpen = remember(url) { lastPage ?: url }
    // no ad banner while a ראש בראש room is open: a timed game, where a stray tap on an ad costs the turn
    val onCurrentPageChanged = remember(isDuelEnabled, pageOrigin) {
        { current: String? ->
            adBannerSuppression.set(isDuelEnabled && isDuelRoomUrl(current))
            if (current?.startsWith(pageOrigin) == true) lastPage = current
        }
    }
    DisposableEffect(adBannerSuppression) {
        onDispose { adBannerSuppression.set(false) }
    }
    var isLoading by remember { mutableStateOf(true) }
    // Real link clicks only (the page moves between its own screens without loading a URL):
    // e.g. the official booklets on the credits page, which a WebView cannot show as PDF.
    val onLinkFailure = remember { { e: Throwable, _: String -> AppCrashlytics.logException(e) } }
    val uriHandler = rememberSafeUriHandler(onFailure = onLinkFailure)

    // Android: the page is drawn under the navigation bar (when no ad banner is above it) and pads itself by the
    // inset; iOS keeps the page above the bottom bar as before.
    val navigationBarInset = LocalNavigationBarInset.current
    Box(
        modifier = Modifier.fillMaxSize().then(if (isAndroid) Modifier else Modifier.navigationBarInsetPadding()),
        contentAlignment = Alignment.Center,
    ) {
        WebView(
            baseUrl = url,
            htmlContent = pageToOpen,
            modifier = Modifier.fillMaxSize(),
            isLoading = { isLoading = it },
            onUrlClicked = { clicked -> clicked?.let(uriHandler::openUri) },
            onCurrentPageChanged = onCurrentPageChanged,
            // the page is an app screen, not a document: no pinch zoom, no on-screen zoom buttons
            isZoomEnabled = false,
            onRootPageBackClicked = onNavigateBack,
            bridge = bridge,
            // the page's own addresses (a reload, e.g. its "רענון" button) stay in the screen
            isPageUrl = { it.startsWith(pageOrigin) },
            bottomInsetCssPx = if (isAndroid) navigationBarInset.value else 0f,
        )
        if (isLoading) {
            CircularProgressIndicator()
        }
    }
}

/** The page follows the app's theme (light parchment or dark navy). */
@Composable
fun isAppInDarkTheme(): Boolean = MaterialTheme.colorScheme.background.luminance() < 0.5f
